import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Heart, ExternalLink, Check, X, Loader2, Calendar, MapPin } from 'lucide-react';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export default function AdminCharityEventsPage() {
  const [tab, setTab] = useState('pending');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/charity-events?status=${status}`);
      setEvents(data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load charity events');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(tab); }, [tab]);

  const approve = async (eventId) => {
    setBusyId(eventId);
    try {
      await api.post(`/admin/events/${eventId}/charity/approve`);
      toast.success('Charity event approved');
      load(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally { setBusyId(null); }
  };

  const reject = async (eventId) => {
    const reason = window.prompt('Optional reason for rejecting the charity claim (the event will keep its listing but lose the Charity badge):', '');
    if (reason === null) return;
    setBusyId(eventId);
    try {
      await api.post(`/admin/events/${eventId}/charity/reject`, { reason });
      toast.success('Charity claim rejected — badge removed');
      load(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally { setBusyId(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-charity-events-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-[#df5681]/15 flex items-center justify-center">
          <Heart className="w-6 h-6 text-[#df5681]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Charity Events Queue</h1>
          <p className="text-sm text-slate-500">Verify nonprofit claims so the Charity Event badge stays meaningful.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            data-testid={`charity-tab-${t.value}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${tab === t.value ? 'bg-[#df5681] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : events.length === 0 ? (
        <Card className="border-slate-100"><CardContent className="text-center py-16 text-slate-400">No {tab} charity events.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {events.map(ev => (
            <Card key={ev.event_id} className="border-slate-100" data-testid={`charity-event-card-${ev.event_id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <Heart className="w-4 h-4 text-[#df5681]" /> {ev.title}
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">Hosted by <strong>{ev.host_name}</strong> ({ev.host_email})</p>
                  </div>
                  <Badge className={`rounded-full whitespace-nowrap ${
                    ev.charity_status === 'approved' ? 'bg-[#56dfb4]/20 text-slate-900' :
                    ev.charity_status === 'rejected' ? 'bg-slate-200 text-slate-700' :
                    'bg-amber-100 text-amber-800'
                  }`}>{ev.charity_status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span>{ev.event_date} {ev.start_time && `at ${ev.start_time}`}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span>{ev.venue_name}, {ev.city} {ev.state}</span>
                  </div>
                </div>

                <div className="rounded-xl p-3 bg-[#df5681]/5 border border-[#df5681]/20 space-y-2 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#df5681] font-medium">Nonprofit</p>
                    <p className="font-medium">{ev.charity_nonprofit_name || <span className="text-slate-400 italic">(not provided)</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#df5681] font-medium">Website</p>
                    {ev.charity_website ? (
                      <a href={ev.charity_website} target="_blank" rel="noopener noreferrer" className="text-[#df5681] hover:underline inline-flex items-center gap-1">
                        {ev.charity_website} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <p className="text-slate-400 italic">(not provided)</p>}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#df5681] font-medium">How proceeds help</p>
                    <p className="text-slate-700">{ev.charity_description || <span className="text-slate-400 italic">(not provided)</span>}</p>
                  </div>
                  {ev.charity_flyer_url && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#df5681] font-medium">Flyer / extra info</p>
                      <a href={ev.charity_flyer_url} target="_blank" rel="noopener noreferrer" className="text-[#df5681] hover:underline inline-flex items-center gap-1">
                        {ev.charity_flyer_url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>

                {ev.charity_status === 'rejected' && ev.charity_rejection_reason && (
                  <p className="text-xs text-slate-500 italic">Rejection note: {ev.charity_rejection_reason}</p>
                )}

                <div className="flex flex-wrap gap-2 pt-1">
                  <Link to={`/events/${ev.event_id}`}>
                    <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`charity-view-${ev.event_id}`}>
                      View event <ExternalLink className="w-3 h-3" />
                    </Button>
                  </Link>
                  {ev.charity_status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => approve(ev.event_id)} disabled={busyId === ev.event_id} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3fc99c] gap-1.5" data-testid={`charity-approve-${ev.event_id}`}>
                        {busyId === ev.event_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                      </Button>
                      <Button size="sm" onClick={() => reject(ev.event_id)} disabled={busyId === ev.event_id} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 gap-1.5" data-testid={`charity-reject-${ev.event_id}`}>
                        <X className="w-3 h-3" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
